import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, FormSection, Grid, RadioGroup, Select, Stack, t, TextField } from '@kanbios/ui';
import { PullRequest, PullRequestNotice } from '../catalogue/PullRequestNotice';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { useSession } from '../../lib/session';
import { AttachmentDraft, AttachmentField, FIELDS, ImportOptions } from './Attachments';
import { PendingButton } from '../PendingButton';

const EMPTY = { provider: '', address: '', name: '', region: '', os: '' };

/**
 * Saisie manuelle (SPEC-007): a server of a provider without a connector, or
 * on site. It is declared with its attachment by a pull request; once merged,
 * it shows in Découverte and is enrolled like the others (sponsor decision,
 * 17 October 2026).
 */
export function ManualEntry({ options }: { options: ImportOptions | undefined }) {
  const { enums } = useSession();
  const [server, setServer] = useState(EMPTY);
  const [attachment, setAttachment] = useState<Omit<AttachmentDraft, 'server'>>({ environment: 'prod', team: '', criticality: 'standard', deployment: null });
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [opened, setOpened] = useState<PullRequest | null>(null);
  const body = { manual: server, attachments: [{ server: server.name.trim(), ...attachment }] };
  const preview = useQuery({
    queryKey: ['server-import-manual', body],
    queryFn: async () => unwrap(await api.maintenance.POST('/servers/imports/preview', { body })),
    placeholderData: keepPreviousData,
    enabled: tried,
  });
  const errors = preview.data?.errors ?? [];
  const errorOf = (field: string) => (tried ? errors.find(e => e.field === field)?.message : undefined);
  const value: AttachmentDraft = { server: server.name.trim(), ...attachment };
  const set = (field: keyof typeof EMPTY) => (e: { target: { value: string } }) => setServer(s => ({ ...s, [field]: e.target.value }));

  const submit = async () => {
    setTried(true);
    setFailure(null);
    const checked = unwrap(await api.maintenance.POST('/servers/imports/preview', { body }));
    if (checked.errors.length) return;
    setSending(true);
    try {
      setOpened(unwrap(await api.maintenance.POST('/servers/imports', { body })).pullRequest);
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };

  if (opened) {
    return (
      <Stack gap={16}>
        <PullRequestNotice pullRequest={opened} />
        <Alert tone="info" title={t('import.manual.nextTitle', { server: server.name })}>{t('import.manual.nextBody')}</Alert>
        <div><Button variant="secondary" icon="add" onClick={() => { setOpened(null); setServer(EMPTY); setTried(false); }}>{t('import.manual.another')}</Button></div>
      </Stack>
    );
  }
  if (options && !options.writable) {
    return <Alert tone="info" title={t('import.manual.notWritableTitle')}>{t('import.attachment.notWritableBody')}</Alert>;
  }
  return (
    <Stack gap={16}>
      {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
      <FormSection layout="stacked" headingLevel={3} title={t('import.manual.serverTitle')} description={t('import.manual.serverDescription')}>
        <RadioGroup inline label={t('import.manual.provider')} options={enums.providers} value={server.provider || undefined}
          onChange={v => setServer(s => ({ ...s, provider: v }))} error={errorOf('provider')} />
        <Grid columns="2" gap={16}>
          <TextField label={t('import.manual.address')} value={server.address} onChange={set('address')} required hint={t('import.manual.addressHint')} error={errorOf('address')} />
          <TextField label={t('import.manual.name')} mono value={server.name} onChange={set('name')} required hint={t('import.manual.nameHint')} error={errorOf('name')} />
          <Select label={t('import.manual.region')} searchable required options={enums.regions.map(r => ({ value: r.value, label: r.label }))}
            value={server.region || null} onChange={v => setServer(s => ({ ...s, region: v ?? '' }))} error={errorOf('region')} />
          <TextField label={t('import.manual.os')} value={server.os} onChange={set('os')} optional hint={t('import.manual.osHint')} />
        </Grid>
      </FormSection>
      <FormSection layout="stacked" headingLevel={3} title={t('import.manual.attachmentTitle')} description={t('import.manual.attachmentDescription')}>
        <Grid columns="2" gap={16}>
          {FIELDS.map(field => (
            <AttachmentField key={field} field={field} value={value} options={options} errors={tried ? errors : []}
              onChange={({ server: _ignored, ...rest }) => setAttachment(rest)} />
          ))}
        </Grid>
        {tried && (preview.data?.warnings ?? []).map(w => <Alert key={w.message} tone="warn">{w.message}</Alert>)}
      </FormSection>
      <div>
        <PendingButton icon="merge" pending={sending} onClick={() => void submit()}>{t('import.manual.submit')}</PendingButton>
      </div>
    </Stack>
  );
}
